import {
  createSlice,
  createAsyncThunk,
  SerializedError,
} from '@reduxjs/toolkit';
import { LOCATION_CHANGE } from 'connected-react-router';
import pick from 'lodash/pick';

import { RootState, AppThunkConfig } from '../../app/store';
import { MemberSummary, EntityRegisterOptions } from '../../types';
import { bindAPI } from '../../app/auth';
import { generatePath, route_path } from '../../app/route';
import {
  ListMembersParams,
  ListMembersResponse,
  listMembers,
  CreateMemberParams,
  CreateMemberResponse,
  createMember,
  GetMemberParams,
  GetMemberResponse,
  getMember,
  UpdateMemberParams,
  UpdateMemberResponse,
  updateMember,
  DeleteMemberParams,
  DeleteMemberResponse,
  deleteMember,
  CreateUserKeyParams,
  CreateUserKeyResponse,
  createUserKey,
  DeleteUserKeyParams,
  DeleteUserKeyResponse,
  deleteUserKey,
} from '../../handlers';
import { logoutAction, hasScope } from '../auth/authSlice';
import {
  setCurrentTenantId,
  selectCurrentTenantId,
} from '../user/userSlice';
import { Member } from '../../types';
import { stripEmptyString } from '../../utils/mutate.util';
import { Page } from '../../utils/page.util';

export type MemberState = {
  list_members: MemberSummary[];
  list_page: Page;
  list_status: 'idle'|'loading'|'failed'|'success';
  list_error?: SerializedError;
  get_status: 'idle'|'loading'|'failed'|'success';
  get_result?: ReturnType<typeof getMemberAction.fulfilled>|ReturnType<typeof getMemberAction.rejected>;
  create_status: 'idle'|'loading'|'failed'|'success';
  create_result?: ReturnType<typeof createMemberAction.fulfilled>|ReturnType<typeof createMemberAction.rejected>;
  update_status:  'idle'|'loading'|'failed'|'success';
  update_result?: ReturnType<typeof updateMemberAction.fulfilled>|ReturnType<typeof updateMemberAction.rejected>;
  delete_status:  'idle'|'loading'|'failed'|'success';
  delete_result?: ReturnType<typeof deleteMemberAction.fulfilled>|ReturnType<typeof deleteMemberAction.rejected>;
};

const initialState: MemberState = {
  list_members: [],
  list_page: {
    page: 1,
    pre_page: 10,
    total_count: 10,
  },
  list_status: 'idle',
  get_status: 'idle',
  create_status: 'idle',
  update_status: 'idle',
  delete_status: 'idle',
};

export type ListMembersActionParams = ListMembersParams;
export type ListMembersActionResponse = ListMembersResponse;
export const listMembersAction = createAsyncThunk<
  ListMembersActionResponse, ListMembersActionParams, AppThunkConfig
>(
  'member/list',
  (params, thunkAPI) => bindAPI(listMembers, thunkAPI)(params),
);

export type CreateMemberActionParams = CreateMemberParams;
export type CreateMemberActionResponse = CreateMemberResponse;
export const createMemberAction = createAsyncThunk<CreateMemberActionResponse, CreateMemberActionParams, AppThunkConfig>(
  'member/create',
  (params, thunkAPI) => {
    return bindAPI(createMember, thunkAPI)({
      tenant_id: params.tenant_id,
      body: pick(stripEmptyString(params.body), [
        'name', 'username', 'password', 'description', 'state',
      ]),
    });
  }
);

export type GetMemberActionParams = GetMemberParams;
export type GetMemberActionResponse = GetMemberResponse;
export const getMemberAction = createAsyncThunk<GetMemberActionResponse, GetMemberActionParams, AppThunkConfig>(
  'user/get',
  (params, thunkAPI) => bindAPI(getMember, thunkAPI)(params)
);

export type UpdateMemberActionParams = UpdateMemberParams;
export type UpdateMemberActionResponse = UpdateMemberResponse;
export const updateMemberAction = createAsyncThunk<UpdateMemberActionResponse, UpdateMemberActionParams, AppThunkConfig>(
  'member/update',
  (params, thunkAPI) => {
    let body = { ...params.body };
    if (body.username === '') {
      body.username = undefined;
    }
    return bindAPI(updateMember, thunkAPI)({
      tenant_id: params.tenant_id,
      user_id: params.user_id,
      body: pick(stripEmptyString(body, { convert_to_null: true }), [
        'name', 'username', 'password', 'description', 'state',
      ]),
    });
  }
);

export type DeleteMemberActionParams = DeleteMemberParams;
export type DeleteMemberActionResponse = DeleteMemberResponse;
export const deleteMemberAction = createAsyncThunk<DeleteMemberActionResponse, DeleteMemberActionParams, AppThunkConfig>(
  'member/delete',
  (params, thunkAPI) => bindAPI(deleteMember, thunkAPI)(params)
);

export type CreateUserKeyActionParams = CreateUserKeyParams;
export type CreateUserKeyActionResponse = CreateUserKeyResponse;
export const createUserKeyAction = createAsyncThunk<CreateUserKeyActionResponse, CreateUserKeyActionParams, AppThunkConfig>(
  'member-keys/create',
  (params, thunkAPI) => bindAPI(createUserKey, thunkAPI)(params)
);

export type DeleteUserKeyActionParams = DeleteUserKeyParams;
export type DeleteUserKeyActionResponse = DeleteUserKeyResponse;
export const deleteUserKeyAction = createAsyncThunk<DeleteUserKeyActionResponse, DeleteUserKeyActionParams, AppThunkConfig>(
  'member-keys/delete',
  (params, thunkAPI) => bindAPI(deleteUserKey, thunkAPI)(params)
);

export const memberSlice = createSlice({
  name: 'member',
  initialState,
  reducers: {
    clearListMemberPageAction: (state) => {
      state.list_page.page = initialState.list_page.page;
      state.list_page.pre_page = initialState.list_page.pre_page;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(listMembersAction.pending, (state, action) => {
        state.list_status = 'loading';
        state.list_page.page = action.meta.arg.page;
        state.list_page.pre_page = action.meta.arg.pre_page;
      })
      .addCase(listMembersAction.fulfilled, (state, action) => {
        state.list_status = 'idle';
        state.list_members = action.payload.data;
        state.list_page.total_count = action.payload.total_count;
      })
      .addCase(listMembersAction.rejected, (state, action) => {
        state.list_status = 'failed';
        state.list_error = action.error;
      })
      .addCase(getMemberAction.pending, (state) => {
        state.get_status = 'loading';
      })
      .addCase(getMemberAction.fulfilled, (state, action) => {
        state.get_status = 'idle';
        state.get_result = action;
      })
      .addCase(getMemberAction.rejected, (state, action) => {
        state.get_status = 'failed';
        state.get_result = action;
      })
      .addCase(createMemberAction.pending, (state) => {
        state.create_status = 'idle';
      })
      .addCase(createMemberAction.fulfilled, (state, action) => {
        state.create_status = 'success';
        state.create_result = action;
      })
      .addCase(createMemberAction.rejected, (state, action) => {
        state.create_status = 'failed';
        state.create_result = action;
      })
      .addCase(updateMemberAction.pending, (state) => {
        state.update_status = 'idle';
      })
      .addCase(updateMemberAction.fulfilled, (state, action) => {
        state.update_status = 'success';
        state.update_result = action;
      })
      .addCase(updateMemberAction.rejected, (state, action) => {
        state.update_status = 'failed';
        state.update_result = action;
      })
      .addCase(deleteMemberAction.pending, (state) => {
        state.delete_status = 'idle';
      })
      .addCase(deleteMemberAction.fulfilled, (state, action) => {
        state.delete_status = 'success';
        state.delete_result = action;
      })
      .addCase(deleteMemberAction.rejected, (state, action) => {
        state.delete_status = 'failed';
        state.delete_result = action;
      })
      .addCase(logoutAction, (state) => initialState)
      .addCase(setCurrentTenantId, (state) => initialState)
      .addCase(LOCATION_CHANGE, (state) => initialState);
  },
});

export const { clearListMemberPageAction } = memberSlice.actions;

export const selectListMembers = (state: RootState) => state.member.list_members;
export const selectMemberSummaryById = (state: RootState, id: string) => {
  return state.member.list_members.find(member => member.id === id);
};
export const selectListMemberPage = (state: RootState) => state.member.list_page;
export const selectCreateMemberResult = (state: RootState) => state.member.create_result;
export const selectUpdateMemberResult = (state: RootState) => state.member.update_result;
export const selectGetMemberResult = (state: RootState) => state.member.get_result;
export const selectMemberActionById = (state: RootState, id: string) => {
  const tenant_id = selectCurrentTenantId(state);
  const member = selectMemberSummaryById(state, id);
  if (!member) {
    return {
      can_edit: false,
      can_view: false,
      can_enable: false,
      can_disable: false,
      can_delete: false,
    };
  }
  return {
    can_edit: hasScope(state, 'auth-portal/user_members.update') && canActionByMember(member, 'edit', tenant_id),
    can_view: hasScope(state, 'auth-portal/user_members.get') && canActionByMember(member, 'view', tenant_id),
    can_enable: hasScope(state, 'auth-portal/user_members.update') && canActionByMember(member, 'enable', tenant_id),
    can_disable: hasScope(state, 'auth-portal/user_members.update') && canActionByMember(member, 'disable', tenant_id),
    can_delete: hasScope(state, 'auth-portal/user_members.delete') && canActionByMember(member, 'delete', tenant_id),
  };
};

const canActionByMember = (
  member: Pick<MemberSummary, 'state'|'tenant_id'>,
  action: 'edit'|'view'|'enable'|'disable'|'delete',
  tenant_id: string,
) => {
  if (action === 'edit') {
    return member.tenant_id === tenant_id;
  } else if (action === 'view') {
    return member.tenant_id === tenant_id;
  } else if (action === 'enable') {
    return member.state === 'disabled' && member.tenant_id === tenant_id;
  } else if (action === 'disable') {
    return member.state === 'enabled' && member.tenant_id === tenant_id;
  } else if (action === 'delete') {
    return member.tenant_id === tenant_id;
  }
  return false;
};

export const selectCanListMembers = (state: RootState) => {
  return hasScope(state, 'auth-portal/user_members.list');
};

export const getMemberListUrl = (tenant_id: string) => generatePath(route_path.member_list, { tenant_id });
export const getMemberCreateUrl = (tenant_id: string) => generatePath(route_path.member_detail, {
  tenant_id, mode: 'create',
});
export const getMemberDetailUrl = (tenant_id: string, user_id: string, mode: 'edit'|'view') => {
  return generatePath(route_path.member_detail, {
    tenant_id, mode,
  })+`?id=${user_id}`;
};

export const member_field_options: EntityRegisterOptions<Member & { password?: string }> = {
  name: {
    maxLength: {
      value: 64,
      message: 'Length must be less than or equal to 64 characters long',
    },
  },
  username: { 
    required: 'Required',
    minLength: {
      value: 4,
      message: 'Length must be at least 4 characters long',
    },
    maxLength: {
      value: 32,
      message: 'Length must be less than or equal to 32 characters long',
    },
    pattern: {
      value: /^[a-z0-9._-]*$/,
      message: 'Fails to match the required pattern: /^[a-z][a-z_.0-9]*$/',
    },
  },
  password: {
    required: 'Required',
    minLength: {
      value: 8,
      message: 'Length must be at least 8 characters long',
    },
    maxLength: {
      value: 32,
      message: 'Length must be less than or equal to 32 characters long',
    },
  },
  description: { 
    maxLength: {
      value: 2048,
      message: 'Length must be less than or equal to 2048 characters long',
    },
  },
};

export const displayEmailState = (user: Pick<Member, 'email_verified'>) => {
  if (user.email_verified) {
    return 'Verified';
  }
  return 'Unverified';
}

export function download(content: string, fileName: string, contentType: string) {
  const a = document.createElement("a");
  const file = new Blob([content], { type: contentType });
  a.href = URL.createObjectURL(file);
  a.download = fileName;
  a.click();
}
